import { useEffect, useMemo } from "react";
import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  Row,
  Space,
  Select,
  Switch,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
 import {
  createRole,
  getPermissions,
  getRoleCodes,
  updateRole,
} from "../../../core/store/reducers/role.slice";
import { showNotification } from "../../../shared/utils/utils";
import { RootState } from "../../../shared/types/common";
import PermissionMatrixComponent from "./permission-matrix.component";

const toPermissionKey = (permissionName: string) =>
  permissionName
    .replace(":", ".")
    .split(".")
    .map((part) => part.toLowerCase().replace(/\s+/g, "-"))
    .join(".");

interface CreateEditRoleProps {
  visible: boolean;
  onClose: () => void;
  selectedEntity: any;
  onSubmit: () => void;
}

const CreateEditRoleComponent: React.FC<CreateEditRoleProps> = ({
  visible,
  onClose,
  selectedEntity,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { loading, roleCodes, permissions: allPermissionsList } = useSelector(
    (state: RootState) => state.role
  );



  useEffect(() => {   
    dispatch(getPermissions());    
  }, [dispatch]);

  // const { modules, permissions: permissionTypes } = useMemo(() => {
  //   const uniqueModules = [...new Set(allPermissionsList.map((p: any) => p.name.split(':')[0]))];
  //   const uniquePermissions = [...new Set(allPermissionsList.map((p: any) => p.name.split(':')[1]))];

  //   return {
  //       modules: uniqueModules.map(m => ({ key: m.toLowerCase().replace(/\s/g, '-'), name: m })),
  //       permissions: uniquePermissions.map(p => ({ key: p.toLowerCase(), name: p }))
  //   };
  // }, [allPermissionsList]);



//   const { modules, permissions: permissionTypes } = useMemo(() => {
//   const uniqueModules = Array.from(
//     new Set<string>(
//       allPermissionsList.map((item: any) =>
//         String(item.name).split(":")[0]
//       )
//     )
//   );

//   const uniquePermissions = Array.from(
//     new Set<string>(
//       allPermissionsList.map((item: any) =>
//         String(item.name).split(":")[1]
//       )
//     )
//   );

//   const modules = uniqueModules.map((m: string) => ({
//     key: m.toLowerCase().replace(/\s/g, "-"),
//     name: m,
//   }));

//   const permissions = uniquePermissions.map((p: string) => ({
//     key: p.toLowerCase(),
//     name: p,
//   }));

//   return { modules, permissions };
// }, [allPermissionsList]);
  

  const { modules, permissions: permissionTypes } = useMemo(() => {
    const permissionList = Array.isArray(allPermissionsList)
      ? allPermissionsList
      : [];

    const uniqueModules = [
      ...new Set(
        permissionList
          .map((item: any) => item?.name?.split?.(".")?.[0])
          .filter(Boolean)
      ),
    ];

    const uniquePermissions = [
      ...new Set(
        permissionList
          .map((item: any) => item?.name?.split?.(".")?.[1])
          .filter(Boolean)
      ),
    ];

    const permissions = uniquePermissions.map((permission: string) => ({
          key: permission.toLowerCase(),
          name: permission,
        }));

   const modules = uniqueModules.map((module: string) => ({
                      key: module.toLowerCase().replace(/\s+/g, "-"),
                      name: module,
                      permissions: permissionList
                        .filter((item: any) => item?.name?.split?.(".")?.[0] === module)
                        .map((item: any) => item?.name?.split?.(".")?.[1])
                        .filter(Boolean),
                    }));
    return {
      modules,
      permissions,
    };
  }, [allPermissionsList]);

useEffect(() => {
    if (selectedEntity) {      
          const initialPermissions = (selectedEntity?.permissions || []).map((p: any) => {
          return toPermissionKey(String(p?.name || ""));
          });

      form.setFieldsValue({
        name: selectedEntity.name,
        code: selectedEntity.code,
        description: selectedEntity.description,
        status: selectedEntity.status,
        permissions: initialPermissions,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ status: true });
    }
  }, [selectedEntity, form, modules, permissionTypes]);

  const onFinish = (values: any) => {
    const selectedPermissions = values.permissions || [];
    const permissionMap = new Map(
      (allPermissionsList || []).map((p: any) => [
        toPermissionKey(String(p.name)),
        p.id,
      ])
    );

    const permissionIds = selectedPermissions
      .map((perm: string) => permissionMap.get(perm.toLowerCase()))
      .filter(Boolean);

   // const payload = { ...values, permissions: permissionIds };
    const payload = {
      ...values,
      status: values.status ? "active" : "inactive", // Transform status to "active" or "inactive"
      permissions: permissionIds };

    if (selectedEntity) {
     (dispatch(updateRole({ id: selectedEntity.id, body: payload })) as any).then(
        (res: any) => {
          if (res.payload?.data) {
            showNotification("Role updated successfully", "success");
            onSubmit();
          }
        }
      );
    } else {
      (dispatch(createRole(payload)) as any).then((res: any) => {
        if (res.payload?.data) {
          showNotification("Role created successfully", "success");
          onSubmit();
        }
      });
    }
  };

  return (
    <Drawer
      title={selectedEntity ? "Edit Role" : "Create Role"}
      width={720}
      onClose={onClose}
      open={visible}
      bodyStyle={{ paddingBottom: 80 }}
      footer={
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
        }}
      >
        <Button
          onClick={() => form.resetFields()}
        >
          Clear
        </Button>

        <Button onClick={onClose}>
          Cancel
        </Button>

        <Button
          type="primary"
          loading={loading}
          onClick={() => form.submit()}
        >
          {selectedEntity ? "Update" : "Submit"}
        </Button>
      </div>
    }
  
    >
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Row gutter={16}>
          <Col span={10}>
            <Form.Item
              name="name"
              label="Role Name"
              rules={[{ required: true, message: "Please enter role name" }]}
            >
              <Input placeholder="Please enter role name" />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              name="code"
              label="Role Type"
              rules={[{ required: true, message: "Please enter role type" }]}
            >
              <Input placeholder="Please enter role type" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="status" label="Status" valuePropName="checked" initialValue={true}>
              <Switch defaultChecked />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="description" label="Description">
              <Input.TextArea
                rows={4}
                placeholder="Please enter role description"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <h3>Permissions</h3>
            <Form.Item name="permissions">
                <PermissionMatrixComponent
                    modules={modules}
                    permissions={permissionTypes}
                />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

export default CreateEditRoleComponent;
